// The hull's plating (Captain Grey, 2026-10-13: "PBR materials + real textures on the hull (metal/roughness, normal maps)
// instead of flat shading"). The textures are made by tools/make_hull_textures.py (nothing bought or downloaded): one
// tileable 10 m square of panels, seams, rivets, hatches, grime and worn edges, in neutral grey so each hull part keeps its
// own colour (the pearl wing frame, the gloss-black frame, the blue-grey skin) and the plating shows through it.
// The hull was built by script with no UVs, so each triangle gets box-projected UVs from its world position: the axis its
// face looks along picks the projection, and a vertex shared by faces that project differently is split. One texture
// tile covers HULL_TILE_M metres everywhere. Stock PBR (albedo + metal/rough/AO + normal map), so WebGPU and WebGL2 both
// run it with no shader of our own. Off on phones by default (Settings > Graphics > Hull detail).
import { type AbstractMesh, Mesh, PBRMaterial, type Scene, Texture, Vector3, VertexBuffer, VertexData } from "@babylonjs/core";

export const HULL_TILE_M = 24;                       // keep in step with TILE_M in tools/make_hull_textures.py (10 m read as a quilt from outside)
export const HULL_MESH = /^(Fuselage_|WingFrame_)/;  // the outer hull: the skin, its ducts and the wing frame (not the rooms inside)
// how each hull part wears the plating: roughness and metal scale the texture's (the texture holds the variation)
const LOOK: Record<string, { rough: number; metal: number; bump: number }> = {
  FramePearl: { rough: 0.55, metal: 1.0, bump: 0.9 },       // the white wing frame: painted, satin
  FrameGlossBlack: { rough: 0.35, metal: 1.0, bump: 0.7 },  // the black frame: glossy, so the plating reads in its highlights
  Fuselage: { rough: 0.8, metal: 1.0, bump: 1.0 },          // the blue-grey skin
  AftWall: { rough: 0.85, metal: 1.0, bump: 1.0 },          // the dark ducts
};

export class HullDetail {
  private originals = new Map<AbstractMesh, PBRMaterial>();
  private made = new Map<string, PBRMaterial>();
  private uvDone = new Set<AbstractMesh>();
  private tex: { albedo: Texture; orm: Texture; normal: Texture } | null = null;
  on = false;
  constructor(private scene: Scene, private meshes: AbstractMesh[]) {}

  set(on: boolean) {
    if (on === this.on) return; this.on = on;
    for (const m of this.meshes) {
      if (!(m instanceof Mesh) || !HULL_MESH.test(m.name) || !(m.material instanceof PBRMaterial || this.originals.has(m))) continue;
      if (on) {
        const orig = this.originals.get(m) ?? (m.material as PBRMaterial); this.originals.set(m, orig);
        if (!this.uvDone.has(m)) { boxUVs(m, HULL_TILE_M); this.uvDone.add(m); }
        m.material = this.plated(orig);
      } else {
        const orig = this.originals.get(m); if (orig) m.material = orig;
      }
    }
  }
  // a plated copy of a hull material (one per original, shared by every mesh that wore it)
  private plated(orig: PBRMaterial): PBRMaterial {
    const have = this.made.get(orig.name); if (have) return have;
    const t = this.tex ??= { albedo: new Texture("/assets/hull/hull_albedo.jpg", this.scene), orm: new Texture("/assets/hull/hull_orm.jpg", this.scene), normal: new Texture("/assets/hull/hull_normal.jpg", this.scene) };
    t.orm.gammaSpace = false; t.normal.gammaSpace = false;           // data, not colour
    const look = LOOK[orig.name] ?? { rough: 0.7, metal: 1.0, bump: 1.0 };
    const m = new PBRMaterial(orig.name + "_plated", this.scene);
    m.albedoColor = orig.albedoColor.clone(); m.albedoTexture = t.albedo;
    m.metallicTexture = t.orm; m.useAmbientOcclusionFromMetallicTextureRed = true; m.useRoughnessFromMetallicTextureGreen = true; m.useMetallnessFromMetallicTextureBlue = true;
    m.metallic = look.metal; m.roughness = look.rough;
    m.bumpTexture = t.normal; m.bumpTexture.level = look.bump;
    m.emissiveColor = orig.emissiveColor.clone(); m.backFaceCulling = orig.backFaceCulling; m.alpha = orig.alpha;
    m.directIntensity = orig.directIntensity; m.environmentIntensity = orig.environmentIntensity;
    this.made.set(orig.name, m); return m;
  }
}

// box-projected UVs in world metres / tile: per triangle, the dominant axis of its face normal picks the plane
function boxUVs(m: Mesh, tile: number) {
  const pos = m.getVerticesData(VertexBuffer.PositionKind), nrm = m.getVerticesData(VertexBuffer.NormalKind), ind = m.getIndices();
  if (!pos || !ind) return;
  if (m.geometry && m.geometry.meshes.length > 1) m.makeGeometryUnique();   // the UVs come from this mesh's own place in the world
  const wm = m.computeWorldMatrix(true), P: number[] = [], N: number[] = [], U: number[] = [], I: number[] = [];
  const w = new Vector3(), split = new Map<number, number>();   // (vertex * 3 + axis) -> new index
  const world: number[] = [];
  for (let i = 0; i < pos.length; i += 3) { Vector3.TransformCoordinatesFromFloatsToRef(pos[i], pos[i + 1], pos[i + 2], wm, w); world.push(w.x, w.y, w.z); }
  const a = new Vector3(), b = new Vector3(), c = new Vector3(), e1 = new Vector3(), e2 = new Vector3(), fn = new Vector3();
  for (let t = 0; t < ind.length; t += 3) {
    const v0 = ind[t], v1 = ind[t + 1], v2 = ind[t + 2];
    a.set(world[v0 * 3], world[v0 * 3 + 1], world[v0 * 3 + 2]); b.set(world[v1 * 3], world[v1 * 3 + 1], world[v1 * 3 + 2]); c.set(world[v2 * 3], world[v2 * 3 + 1], world[v2 * 3 + 2]);
    b.subtractToRef(a, e1); c.subtractToRef(a, e2); Vector3.CrossToRef(e1, e2, fn);
    const ax = Math.abs(fn.x), ay = Math.abs(fn.y), az = Math.abs(fn.z), axis = ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
    for (const v of [v0, v1, v2]) {
      const key = v * 3 + axis; let ni = split.get(key);
      if (ni === undefined) {
        ni = P.length / 3; split.set(key, ni);
        P.push(pos[v * 3], pos[v * 3 + 1], pos[v * 3 + 2]); if (nrm) N.push(nrm[v * 3], nrm[v * 3 + 1], nrm[v * 3 + 2]);
        const x = world[v * 3], y = world[v * 3 + 1], z = world[v * 3 + 2];
        const [u, vv] = axis === 0 ? [z, y] : axis === 1 ? [x, z] : [x, y];
        U.push(u / tile, vv / tile);
      }
      I.push(ni);
    }
  }
  const vd = new VertexData(); vd.positions = P; if (nrm) vd.normals = N; vd.uvs = U; vd.indices = I;
  const frozen = m.isWorldMatrixFrozen; vd.applyToMesh(m, false);
  if (frozen) m.freezeWorldMatrix();
}
